import React, { createContext, useContext, useState, useEffect } from "react";
import {
  testAsanaAuth,
  fetchCustomFieldDefinitions,
  fetchSections,
  fetchAllTasks,
} from "../pages/deal-tracker/data/client";

// Create the context
export const AsanaContext = createContext();

export const AsanaProvider = ({ children }) => {
  // State for Asana data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asanaDeals, setAsanaDeals] = useState([]);
  const [sections, setSections] = useState([]);
  const [customFieldsMeta, setCustomFieldsMeta] = useState([]);
  const [dealsBySection, setDealsBySection] = useState({});
  const [filteredSections, setFilteredSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});

  // Function to match deal names to Asana section names
  const matchDealToSection = (dealName) => {
    if (!sections.length || !dealName) return null;

    // Normalize the input deal name
    const normalizedDealName = dealName.toLowerCase().trim();

    // First try direct match with section names
    let matchedSection = sections.find(
      (section) => section.name.toLowerCase().trim() === normalizedDealName
    );

    if (matchedSection) {
      // Get tasks for this section
      const tasksInSection = dealsBySection[matchedSection.id] || [];
      if (tasksInSection.length > 0) {
        return {
          section: matchedSection,
          tasks: tasksInSection,
        };
      }
    }

    // Try removing fire emoji if present in section names
    matchedSection = sections.find((section) => {
      // Remove fire emoji 🔥 and trim whitespace
      const cleanSectionName = section.name
        .replace(/🔥|(\u{1F525})/gu, "")
        .trim()
        .toLowerCase();
      const match = cleanSectionName === normalizedDealName;
      return match;
    });

    if (matchedSection) {
      const tasksInSection = dealsBySection[matchedSection.id] || [];
      if (tasksInSection.length > 0) {
        return {
          section: matchedSection,
          tasks: tasksInSection,
        };
      }
    }

    // Try more flexible matching approaches

    // 1. Check if section name contains the deal name or vice versa
    matchedSection = sections.find((section) => {
      const sectionNameLower = section.name.toLowerCase().trim();
      const containsMatch =
        sectionNameLower.includes(normalizedDealName) ||
        normalizedDealName.includes(sectionNameLower);
      return containsMatch;
    });

    if (matchedSection) {
      const tasksInSection = dealsBySection[matchedSection.id] || [];
      if (tasksInSection.length > 0) {
        return {
          section: matchedSection,
          tasks: tasksInSection,
        };
      }
    }

    // 2. Try matching by removing special characters and extra spaces
    matchedSection = sections.find((section) => {
      const cleanSectionName = section.name
        .toLowerCase()
        .replace(/[^\w\s]/gi, "") // Remove special characters
        .replace(/\s+/g, " ") // Replace multiple spaces with a single space
        .trim();

      const cleanDealName = normalizedDealName
        .replace(/[^\w\s]/gi, "")
        .replace(/\s+/g, " ")
        .trim();

      const match = cleanSectionName === cleanDealName;
      return match;
    });

    if (matchedSection) {
      const tasksInSection = dealsBySection[matchedSection.id] || [];
      if (tasksInSection.length > 0) {
        return {
          section: matchedSection,
          tasks: tasksInSection,
        };
      }
    }

    // 3. Try fuzzy matching (checking if most words match)
    matchedSection = sections.find((section) => {
      const sectionWords = section.name
        .toLowerCase()
        .replace(/[^\w\s]/gi, "")
        .split(/\s+/)
        .filter((word) => word.length > 2); // Only consider words with 3+ chars

      const dealWords = normalizedDealName
        .replace(/[^\w\s]/gi, "")
        .split(/\s+/)
        .filter((word) => word.length > 2);

      // Check if at least 70% of words match
      if (sectionWords.length === 0 || dealWords.length === 0) return false;

      const matchingWords = sectionWords.filter((word) =>
        dealWords.some(
          (dealWord) => dealWord.includes(word) || word.includes(dealWord)
        )
      );

      const matchPercentage = Math.max(
        matchingWords.length / sectionWords.length,
        matchingWords.length / dealWords.length
      );

      const match = matchPercentage >= 0.7; 
      return match;
    });

    if (matchedSection) {
      const tasksInSection = dealsBySection[matchedSection.id] || [];
      if (tasksInSection.length > 0) {
        return {
          section: matchedSection,
          tasks: tasksInSection,
        };
      }
    }

    return null;
  };

  // Function to determine if a section is "hot" (has fire emoji)
  const isSectionHot = (section) => {
    if (!section || !section.name) return false;
    return section.name.includes("🔥") || section.name.includes("\u{1F525}");
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Fetch Asana data
  useEffect(() => {
    const getAsanaData = async () => {
      try {
        setLoading(true);

        // Test authentication first
        const authSuccess = await testAsanaAuth();
        if (!authSuccess) {
          throw new Error(
            "Authentication with Asana failed. Check your access token."
          );
        }

        // First, fetch the custom field definitions
        const customFields = await fetchCustomFieldDefinitions();
        setCustomFieldsMeta(customFields);

        // Fetch sections
        const sectionData = await fetchSections();
        setSections(sectionData);

        // Initialize all sections as expanded
        const expandedState = {};
        sectionData.forEach((section) => {
          expandedState[section.id] = true;
        });
        setExpandedSections(expandedState);

        // Fetch all tasks once, then group by section in memory.
        // Per-section fetches fan out one request per section in parallel,
        // which routinely tripped Asana's rate limit (HTTP 429) on cold loads.
        const tasks = await fetchAllTasks();
        setAsanaDeals(tasks);

        const tasksBySection = {};
        sectionData.forEach((section) => {
          tasksBySection[section.id] = tasks.filter(
            (task) => task.sectionName === section.name
          );
        });
        setDealsBySection(tasksBySection);

        // Filter out empty sections
        const sectionsWithDeals = sectionData.filter(
          (section) =>
            tasksBySection[section.id] && tasksBySection[section.id].length > 0
        );
        setFilteredSections(sectionsWithDeals);

        setError(null);
      } catch (err) {
        console.error("Error fetching Asana data:", err);
        setError(err.message || "Failed to fetch data from Asana");
      } finally {
        setLoading(false);
      }
    };

    getAsanaData();
  }, []);

  // Render status badge component
  const StatusBadge = ({ task }) => {
    const statusText = task.customFields?.Status;
    const statusWithColors = task.customFields?.Status_with_colors;

    if (!statusText) {
      return <span className="text-xs text-gray-500">No status</span>;
    }

    // If we have multiple statuses (using the array format)
    if (Array.isArray(statusWithColors)) {
      return (
        <div className="flex flex-wrap gap-1">
          {statusWithColors.map((status, index) => {
            // Apply hardcoded colors based on status value
            const statusLower = status.name.toLowerCase();
            let backgroundColor;

            if (statusLower.includes("complete")) {
              backgroundColor = "#f8df72";
            } else if (statusLower.includes("sent")) {
              backgroundColor = "#4ecbc4";
            } else if (statusLower.includes("in contract")) {
              backgroundColor = "#4573d2";
            } else if (statusLower.includes("under loi")) {
              backgroundColor = "#f06a6a";
            } else if (statusLower.includes("deal is under review")) {
              backgroundColor = "#ec8d71";
            } else if (statusLower.includes("seller declined")) {
              backgroundColor = "#f1bd6c";
            } else {
              // Use the color from Asana if available, or default to a gray
              backgroundColor =
                status.color && status.color !== "none"
                  ? status.color
                  : "#e2e8f0";
            }

            return (
              <span
                key={`status-${index}`}
                className="inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5"
                style={{
                  backgroundColor,
                  color: "black",
                }}
              >
                {status.name}
              </span>
            );
          })}
        </div>
      );
    }

    // Simplified approach: hardcoded colors for specific status values
    const statusLower = statusText.toLowerCase();
    let backgroundColor;

    // Apply hardcoded colors based on status value
    if (statusLower.includes("complete")) {
      backgroundColor = "#f8df72"; // Yellow for complete
    } else if (statusLower.includes("sent")) {
      backgroundColor = "#4ecbc4"; // Teal for sent
    } else if (statusLower.includes("in contract")) {
      backgroundColor = "#4573d2"; // Blue for in contract
    } else if (statusLower.includes("under loi")) {
      backgroundColor = "#f06a6a";
    } else if (statusLower.includes("deal is under review")) {
      backgroundColor = "#ec8d71";
    } else if (statusLower.includes("seller declined")) {
      backgroundColor = "#f1bd6c";
    }

    // For the hardcoded colors, use black text
    return (
      <span
        className="inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5"
        style={{
          backgroundColor,
          color: "black",
        }}
      >
        {statusText}
      </span>
    );
  };

  return (
    <AsanaContext.Provider
      value={{
        loading,
        error,
        asanaDeals,
        sections,
        customFieldsMeta,
        dealsBySection,
        filteredSections,
        expandedSections,
        toggleSection,
        StatusBadge,
        matchDealToSection,
        isSectionHot,
      }}
    >
      {children}
    </AsanaContext.Provider>
  );
};

export const useAsanaData = () => useContext(AsanaContext);
